import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { stripeService } from '../../../services/stripeService';

const CheckoutForm = ({ selectedPlan, onPaymentSuccess, onCancel }) => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubscribe = async () => {
    if (!user || !selectedPlan) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { data, error: stripeError } = await stripeService?.createSubscription(
        selectedPlan?.stripe_price_id || 'price_1S5TCNE6oTidvpnUyJC5xrAe', // Real Stripe price ID
        {
          userId: user?.id,
          email: user?.email || user?.userProfile?.email,
          name: user?.userProfile?.first_name && user?.userProfile?.last_name 
            ? `${user.userProfile.first_name} ${user.userProfile.last_name}`
            : user?.userProfile?.full_name || user?.name
        }
      );

      if (stripeError) {
        setError(stripeError);
        return;
      }

      if (data?.url) {
        // Redirect to Stripe Checkout
        window.location.href = data?.url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!selectedPlan) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <p className="text-center text-muted-foreground">
          Please select a subscription plan.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Subscribe to {selectedPlan?.name}</h3>
            <p className="text-muted-foreground text-sm">
              Secure checkout powered by Stripe
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-sm text-success">Secure</span>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Order Summary */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium text-foreground">{selectedPlan?.name}</span>
            <span className="font-semibold text-foreground">
              {stripeService?.formatAmount(selectedPlan?.tier === 'premium' ? 1999 : (selectedPlan?.price * 100 || selectedPlan?.amount_cents || 1999))}/{selectedPlan?.period || 'month'}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-3">{selectedPlan?.description}</p>
          
          <div className="space-y-2 mb-4">
            {selectedPlan?.features?.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 text-sm">
                <Icon name="Check" size={14} className="text-success" />
                <span className={`${feature?.included ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                  {feature?.text || feature}
                </span>
                {feature?.highlight && (
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">
                    {feature?.highlight}
                  </span>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex items-center space-x-2 text-sm text-success">
            <Icon name="Shield" size={14} />
            <span>30-day money-back guarantee</span>
          </div>
        </div>

        {/* User Information */}
        <div className="space-y-3">
          <h4 className="font-medium text-foreground">Account Information</h4>
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email:</span>
              <span className="text-foreground">{user?.email || user?.userProfile?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name:</span>
              <span className="text-foreground">
                {user?.userProfile?.first_name && user?.userProfile?.last_name 
                  ? `${user.userProfile.first_name} ${user.userProfile.last_name}`
                  : user?.userProfile?.full_name || user?.name || 'Not provided'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          
          <Button
            type="button"
            variant="default"
            fullWidth
            loading={isProcessing}
            iconName="CreditCard"
            iconPosition="left"
            onClick={handleSubscribe}
            disabled={isProcessing || !selectedPlan}
          >
            {isProcessing ? 'Creating Checkout...' : `Subscribe for ${stripeService?.formatAmount(selectedPlan?.tier === 'premium' ? 1999 : (selectedPlan?.price * 100 || selectedPlan?.amount_cents || 1999))}`}
          </Button>
        </div>

        {/* Security Notice */}
        <div className="text-center text-xs text-muted-foreground">
          <div className="flex items-center justify-center space-x-2 mb-1">
            <Icon name="Lock" size={12} />
            <span>Your payment information is encrypted and secure</span>
          </div>
          <p>Powered by Stripe • PCI DSS Compliant</p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutForm;